
import React, { useState, useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import csv from 'csvtojson'
import XLSX from 'xlsx'
import moment from 'moment'
import _ from 'lodash'
import { getProductionPlan, getScaleTickets } from '../../services/apiHelper'
import Table from '../../dpe-usco/components/Table/Table'
import TableStatus from '../../components/subComponents/TableStatus/TableStatus'
import { Button, Modal } from 'syngenta-react-components'
import CheckGrowerModal from './CheckGrowerModal'
import { getISODateFromExcel } from '../../services/utils'
import { initialStructure, headerCell } from './initStruct'
import { ACCEPTABLE_HEADERS, REQUIRED_HEADERS } from './AcceptableHeaders'
import { AuthContext } from '../../services/context'
import AccessDeniedView from '../Interstitial/AccessDeniedView'
import error from '../../assets/icons/error.svg'
import error_outline from '../../assets/icons/error_outline.svg'
import close from '../../assets/icons/close.svg'

const WarningModal = ({ showWarningModal, setShowWarningModal, numberOfIssues, arrOfMissingRequiredVal, arrOfDupVals, arrOfTicketWithInvalidDates }) => {

  const createList = arr => {
    return arr.map((obj, index) => {
      return (
        <span key={`${index}`}>{`${obj} `}</span>
      )
    })
  }

  const createMissingValuesList = arr => {
    return (
      <div>
        {arr.map((missingValue, index) => {
          return (
            <div className='df' key={`${index}`}>
              <span className='mr24'>
                {missingValue.index}
              </span>
              <div>
                {missingValue.whatIsMissing.map((val, indexKey) => {return <span className='ml8' key={`${indexKey}`}>{`${val}${index+1 === missingValue.whatIsMissing.length ? '' : ','}`}</span>})}
              </div>  
            </div>  
          )
        })}
      </div>
    )
  }

  return (
    <Modal
      autoHeight
      show={showWarningModal}
      handleClose={() => setShowWarningModal(false)}
    >
      <div className='w100% tal h100% bgc-white bd1-s-lightgray bxshmodal bdrs-4 mb40'>
        <div className='bb1-s-lightgray df jcsb'>
          <div className='df ml15 mt15 mb15'>
            <img src={error_outline}
              alt='error icon'>
            </img>
            <div className='ml10'>
              {`Review Upload Issue${numberOfIssues > 1 ? 's' : ''}`}
            </div>
          </div>
          <img className='mr15 curp' onClick={() => setShowWarningModal(false)} src={close}
            alt='close icon'>
          </img>
        </div>
        <div className='p15'>
          <div>
            <span className='fw600'>{numberOfIssues}</span>
            <span>
              {` issue${numberOfIssues > 1 ? 's' : ''} found. Edit and reupload the corrected source file in order to save. See below for list of identified row${numberOfIssues > 1 ? 's' : ''}.`}
            </span>
            {arrOfMissingRequiredVal?.length ?
              <>
                <div className='fw600 mt25 mb10'>{`MISSING REQUIRED DATA FIELD${arrOfMissingRequiredVal.length > 1 ? 'S' : ''}`}</div>
                {createMissingValuesList(arrOfMissingRequiredVal)}
              </> : null
            }
            {arrOfDupVals?.length ?
              <>
                <div className='fw600 mt25 mb10'>{`DUPLICATE${arrOfDupVals.length > 1 ? 'S' : ''} WITHIN FILE`}</div>
                <div>
                  {createList(arrOfDupVals)}
                </div>
              </> : null
            }
            {arrOfTicketWithInvalidDates?.length ?
              <>
                <div className='fw600 mt25 mb10'>{`TICKET${arrOfTicketWithInvalidDates.length > 1 ? 'S' : ''} WITH INVALID DATE FOUND`}</div>
                <div>
                  {createList(arrOfTicketWithInvalidDates)}
                </div>
              </> : null
            }
          </div>
        </div>
      </div>
    </Modal>
  )
}


const WrongFormatModal = ({ showWrongFormatModal, setShowWrongFormatModal }) => {
  return (
    <Modal
      autoHeight
      show={showWrongFormatModal}
      handleClose={() => setShowWrongFormatModal(false)}
    >
      <div className='w100% tal h100% bgc-white bd1-s-lightgray bxshmodal bdrs-4 mb40'>
        <div className='bb1-s-lightgray df jcsb'>
          <div className='df ml15 mt15 mb15'>
            <img src={error_outline}
              alt='error icon'>
            </img>
            <div className='ml10'>Review Upload Issue</div>
          </div>
          <img className='mr15 curp' onClick={() => setShowWrongFormatModal(false)} src={close}
            alt='close icon'>
          </img>
        </div>
        <div className='df w100% jcfe"'>
          <div className='df tac p60'>Whoops!<br />Wrong file format, please make sure to use the following formats .csv, .xls, or .xlsx.
          </div>
        </div>
      </div>
    </Modal>
  )
}

const DocUploader = ({ setHasUpload, checkGrowerModalVisibility, toggleCheckGrowerModalVisibility, setHasAllRequiredData, setPageRefresh, parentSetTableData }) => {
  const [tableData, setTableData] = useState()
  const [showWarningModal, setShowWarningModal] = useState(false)
  const [showWarningPrompt, setWarningPrompt] = useState(false)
  const [numberOfIssues, setNumberOfIssues] = useState(0)
  const [arrOfMissingRequiredVal, setArrOfMissingRequiredVal] = useState([])
  const [arrOfTicketWithInvalidDates, setArrOfTicketWithInvalidDates] = useState([])
  const [arrOfDupVals, setArrOfDupVals] = useState([])
  const [hasPermission, setHasPermission] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [structure, setStructure] = useState({
    headers: [],
    dataKeys: []
  })
  const [showWrongFormatModal, setShowWrongFormatModal] =  useState(false)
  const { hasAccess } = useContext(AuthContext)
  const [currentProductionYear, setCurrentProductionYear] = useState('')
  const { production_plan_id } = useParams()

  useEffect(() => {
    setHasPermission(hasAccess('ANY', 'create-scale-ticket'))
  }, [hasAccess])

  const [allScaleTickets, setAllScaleTickets] = useState([])
  const [ticketsLoaded, setTicketsLoaded] = useState(false)
  useEffect(() => {
    const PAGE_SIZE = 100
    const normalizeTicket = (obj, index) => {
      delete Object.assign(obj, { 'bol': obj['bill_of_lading'] })['bill_of_lading']
      delete Object.assign(obj, { 'ticketNumber': obj['scale_ticket_number'] })['scale_ticket_number']
      delete Object.assign(obj, { 'grower': obj['grower_name'] })['grower_name']
      delete Object.assign(obj, { 'date': moment(obj['scale_ticket_date']).format('MM/DD/YY') })['scale_ticket_date']
      delete Object.assign(obj, { 'FM': obj['foreign_material'] })['foreign_material']
      delete Object.assign(obj, { 'Gross Qty': obj['gross_quantity'] })['gross_quantity']
      delete Object.assign(obj, { 'Gross Weight': obj['gross_weight'] })['gross_weight']
      delete Object.assign(obj, { 'MST': obj['moisture'] })['moisture']
      delete Object.assign(obj, { 'Net Qty': obj['net_quantity'] })['net_quantity']
      delete Object.assign(obj, { 'Net Weight': obj['net_weight'] })['net_weight']
      delete Object.assign(obj, { 'RVA': obj['rapid_visco_analyzer'] })['rapid_visco_analyzer']
      delete Object.assign(obj, { 'Tare': obj['tare_weight'] })['tare_weight']
      delete Object.assign(obj, { 'Test Weight': obj['test_weight'] })['test_weight']
      delete Object.assign(obj, { 'DMG': obj['damage'] })['damage']
      obj.isAllScaleTicketsArr = true
      obj.Index = index + 1
      return obj
    }

    getProductionPlan(production_plan_id)
      .then(async res => {
        setCurrentProductionYear(res.contract_year)
        setTicketsLoaded(false)
        const accumulated = []
        let lastScaleTicketId = null
        let lastProductionPlanId = null

        while (true) {
          const page = await getScaleTickets(
            production_plan_id,
            'production_plan_id',
            PAGE_SIZE,
            lastScaleTicketId,
            lastProductionPlanId
          )
          const filtered = page.filter(obj => {
            const ticketYear = new Date(obj.scale_ticket_date).getFullYear().toString()
            return ticketYear === res.contract_year.toString()
          })
          filtered.forEach(obj => accumulated.push(obj))

          if (page.length < PAGE_SIZE) break

          const last = page[page.length - 1]
          lastScaleTicketId = last.scale_ticket_id
          lastProductionPlanId = production_plan_id
        }

        accumulated.forEach((obj, index) => normalizeTicket(obj, index))
        setAllScaleTickets(accumulated)
        setTicketsLoaded(true)
      })
  }, [production_plan_id])

  const createTicketNumberLink = dataArray => {
    
    return dataArray.map(obj => {
      let date
      if (obj.Date) {
        date = typeof obj.Date === 'string' ? moment(obj.Date).format('MM/DD/YY') : moment(getISODateFromExcel(obj.Date)).format('MM/DD/YY')
      }
      if(obj.RVA){
        obj['RVA']= obj.RVA < 1 ? '0' : '1'
      }else{
        obj['RVA']= ''
      }
      
      obj['Ticket Number'] = {
        grower_name: obj.Grower,
        scale_ticket_date: date,
        scale_ticket_id: obj['Ticket Number'],
        bill_of_lading: obj.BOL,
        gross_weight: obj['Gross Weight'],
        tare_weight: obj.Tare,
        net_quantity: obj['Net Qty'],
        gross_quantity: obj['Gross Qty'],
        test_weight: obj['Test Weight'],
        moisture: obj.MST,
        foreign_material: obj.FM,
        damage: obj.DMG,
        rapid_visco_analyzer: obj.RVA < 1 ? '0' : '1'
      }
      obj.Date = date
      return obj
    })
  }

  const updateJSONKeys = json => {
    return json.map((obj, index) => {
      for (var oldKey in obj) {
        let newKey = parseHeaders(oldKey)
        if (oldKey !== newKey) {
          Object.defineProperty(obj, newKey,
            Object.getOwnPropertyDescriptor(obj, oldKey))
          delete obj[oldKey]
        }
      }

      const convertToString = obj => {
        const newObj = {}
        Object.entries(obj).forEach(([key, val]) => {
          if (key === 'Date'){
            const convertDate = obj.Date
            return newObj[key] = convertDate ? moment(convertDate).format('MM/DD/YY') : moment(getISODateFromExcel(convertDate)).format('MM/DD/YY')
          }
          return newObj[key] = val?.toString()
        })
        return newObj
      }

      return Object.assign({ 'Index': index + 1 },  convertToString(obj) )
    })
  }

  const checkForErrors = json => {
    const setErrorIcon = obj => {
      if (typeof obj.Index === 'number') {
        obj.Index = (<div className='df jcsb'>{obj.Index} {<img className='mr15' alt='error icon' src={error_outline} />}</div>)
      }
    }
    let ticketWithInvalidDates = []
    let ticketWithMissingValues = []
    let arrToTestDups = []
    let dupIndexMap = {}
    let arrOfDupIndex = []
    json.forEach((obj, index) => {
      const whatIsMissing = []
      for (let i = 0;i < REQUIRED_HEADERS.length;i++) {
        const currentHeader = REQUIRED_HEADERS[i]
        if (!(currentHeader in obj) || !obj[currentHeader]) {
          obj.customRowClass = 'bgc-red-100'
          whatIsMissing.push(REQUIRED_HEADERS[i])
          setErrorIcon(obj)
        }
        if (i === (REQUIRED_HEADERS.length - 1) && obj.customRowClass !== 'bgc-red-100') {
          dupIndexMap[obj.Index] = {
            bol: obj.BOL,
            date: obj.Date,
            grower: obj.Grower.trim(),
            ticketNumber: obj['Ticket Number'],
            netQty: obj['Net Qty']
          }
          arrToTestDups.push({
            bol: obj.BOL,
            date: obj.Date,
            grower: obj.Grower.trim(),
            ticketNumber: obj['Ticket Number'],
            netQty: obj['Net Qty']
          })
        }
      }

      if(!moment(obj.Date,'MM/DD/YY', true).isValid()){
        ticketWithInvalidDates.push(index + 1)
      }
      if (whatIsMissing.length) {
        ticketWithMissingValues.push({ index:index + 1, whatIsMissing })
        setErrorIcon(obj)
      }

      return obj
    })

    if (arrToTestDups.length) {

      const results = []
      const findDuplicates = arr => {
        return arr.filter((item, index) => {
          return arr.some((otherItem, otherIndex) => {
            if (index === otherIndex) return false
            const sameDateGrower = item.date === otherItem.date && item.grower === otherItem.grower
            if (item.bol && item.ticketNumber && otherItem.bol && otherItem.ticketNumber ) {
              return (
                sameDateGrower &&
                item.bol === otherItem.bol &&
                item.ticketNumber === otherItem.ticketNumber
              )
            }
            if (item.bol && otherItem.bol) {
              return (sameDateGrower && item.bol === otherItem.bol)
            }
            if (item.ticketNumber && otherItem.ticketNumber ) {
              return (sameDateGrower && item.ticketNumber === otherItem.ticketNumber)
            }
            return sameDateGrower && item.netQty === otherItem.netQty
          })
        })
      }
      let parsedResults = findDuplicates(arrToTestDups)
      parsedResults = [...parsedResults, ...results]
      arrOfDupIndex = parsedResults.map(obj => {
        let returnVal = []
        for (const [key] of Object.entries(dupIndexMap)) {
          if (dupIndexMap[key].ticketNumber === obj.ticketNumber && dupIndexMap[key].bol === obj.bol && dupIndexMap[key].date === obj.date && dupIndexMap[key].grower === obj.grower && dupIndexMap[key].netQty === obj.netQty) {
            returnVal.push(key)
          }
        }
        return returnVal
      })
    }
    if (arrOfDupIndex.length) {
      arrOfDupIndex = _.flatten(arrOfDupIndex)
    }
    arrOfDupIndex.forEach(dupeIndex => {
      json.forEach(scaleTickObj => {
        if (scaleTickObj.Index.toString() === dupeIndex) {
          scaleTickObj.customRowClass = 'bgc-red-100'
          scaleTickObj.Index = (<div className='df jcsb'>{scaleTickObj.Index} {<img className='mr15' alt='error icon' src={error_outline} />}</div>)
        }
      })
    })
    arrOfDupIndex = [...new Set(arrOfDupIndex)]
    return {
      ticketWithMissingValues,
      arrOfDupIndex,
      ticketWithInvalidDates
    }
  }

  const renderJSON = JSONobj => {
    let headerArr = [
      {
        value: 'Index',
        format() {
          return (<div key='space' className='cell df aic' />)
        }
      }
    ]
    let dataArr = ['Index']
    for (var property in JSONobj[0]) {
      let parsedHeader = parseHeaders(property)
      headerArr.push({
        value: parsedHeader,
        format() {
          return headerCell(this.value)
        }
      })
      dataArr.push(parsedHeader)
    }

    JSONobj = updateJSONKeys(JSONobj)
    let errorObj = checkForErrors(JSONobj)
    if (errorObj?.ticketWithMissingValues?.length || errorObj?.arrOfDupIndex?.length || errorObj?.ticketWithInvalidProductionYear?.length || errorObj?.ticketWithInvalidDates?.length ) {
      setArrOfMissingRequiredVal(errorObj.ticketWithMissingValues)
      setArrOfDupVals(errorObj.arrOfDupIndex)
      setArrOfTicketWithInvalidDates(errorObj.ticketWithInvalidDates)
      setNumberOfIssues((errorObj.ticketWithMissingValues.length || 0) + (errorObj.arrOfDupIndex.length) + (errorObj?.ticketWithInvalidDates?.length) )
      setWarningPrompt(true)
    } else {
      setHasAllRequiredData(true)
    }

    let filterData = createTicketNumberLink(JSONobj)
    setTableData(filterData)
    parentSetTableData(filterData)
    const checkRVAExist = headerArr.filter(ele => ele.value === 'RVA')
   
    if(checkRVAExist.length === 0) {
      const lastHeaderArrItem = headerArr.pop()
      const lastDataArrItem = dataArr.pop()
      headerArr.push({
        value: 'RVA',
        format() {
          return headerCell(this.value)
        }
      })
      dataArr.push('RVA')
      dataArr.push(lastDataArrItem)
      headerArr.push(lastHeaderArrItem)
    }

    const setUpStruc = {
      headers: headerArr,
      dataKeys: dataArr,
      rowFormat: {
        'Ticket Number': {
          format: data => {
            return (
              <span>{data.scale_ticket_id}</span>
            )
          }
        }
      }
    }
    setStructure(setUpStruc)
  }


  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      setShowWrongFormatModal(true)
    } else {
      setIsLoading(true)
      const reader = new FileReader()
      acceptedFiles.forEach(file => {
        reader.onload = () => {

          const fileAsBinaryString = reader.result
          if (acceptedFiles[0] && acceptedFiles[0]?.type.endsWith('csv')) {
            csv({
              noheader: true,
              output: 'json'
            })
              .fromString(fileAsBinaryString)
              .then(csvRows => {
                const toJSON = []
                csvRows.forEach((aCsvRow, i) => {
                  if (i !== 0) {
                    const builtObject = {}
                    Object.keys(aCsvRow).forEach(aKey => {
                      const valueToAddInBuiltObject = aCsvRow[aKey]
                      const keyToAddInBuiltObject = csvRows[0][aKey]
                      builtObject[keyToAddInBuiltObject] = valueToAddInBuiltObject
                    })
                    toJSON.push(builtObject)
                  }
                })
                renderJSON(toJSON)
              })
          } else if (acceptedFiles[0]?.type.endsWith('sheet') || acceptedFiles[0]?.path.endsWith('xls')) {
          
            let workBook = XLSX.read(fileAsBinaryString, { type: 'binary', cellDates: true, cellNF: false, cellText:false })
            let jsonData = workBook.SheetNames.reduce((init, name) => {
              const sheet = workBook.Sheets[name]
              return XLSX.utils.sheet_to_json(sheet, { raw: true, dateNF:'MM-DD-YY' })
            }, {})
            renderJSON(jsonData)
          }
          setHasUpload(true)
          setIsLoading(false)
        }
        reader.readAsBinaryString(file)
      })
    }
  }

  const parseHeaders = header => {
    let returnHeader
    const normalizeHeader = header.toLowerCase().trim().split('.').join('')
    for (const [headerName, headerArray] of Object.entries(ACCEPTABLE_HEADERS)) {
      if (headerArray.includes(normalizeHeader)) {
        returnHeader = headerName
      }
    }
    return returnHeader ? returnHeader : header
  }

  const clear = () => {
    setTableData()
    setStructure({
      headers: [],
      dataKeys: []
    })
    setArrOfDupVals([])
    setHasAllRequiredData(false)
    setArrOfMissingRequiredVal([])
    setHasUpload(false)
    setWarningPrompt(false)
    setNumberOfIssues(0)
  }

  const { open, getRootProps, getInputProps } = useDropzone({ onDrop, noClick: true, noKeyboard: true, accept: '.csv, .xls, .xlsx' })

  const prompt = (
    <div {...getRootProps()}>
      {<p>Drag-and-drop or choose a file to import scale tickets <br /> Supported file types: .csv, .xls, or .xlsx</p>}
      <input {...getInputProps()} />
      <Button text='Choose File' onClick={open} />
    </div>)

  const ButtonRow = () => {
    return (
      <div className='df mt25'>
        <div className='fxdrc aifs'>
          {tableData
            ?
            <>
              <Button text='Clear File' onClick={clear} />
            </>
            :
            <>
              <div className='mb5 fz15'>Upload CSV or XLS</div>
              <Button text='Choose File' onClick={open} />
            </>}
        </div>
        {
          showWarningPrompt ?
            <div className='mauto bd1-s-red bgc-red-100 mb20'>
              <div className='df pt15 pr10 pl10 pb15'>
                <img
                  alt='error icon'
                  src={error} />
                <div className='pr5 pl5 mt5'>
                  {`${numberOfIssues} issue${numberOfIssues > 1 ? 's' : ''} found. Edit and reupload the corrected source file in order to save.`}
                </div>
                <Button customClassName='m0' text={'Review Issue'} clickHandler={() => setShowWarningModal(true)}></Button>
              </div>
            </div> : null

        }

      </div>
    )
  }

  const ScaleTicketsHeader = () => {
    return (
      <div className='df fxdrr jcsb mb24'>
        <div className='df aic'>
          <span className='fw600'>Scale Tickets</span>
          {tableData ? <span className='ml4 fw600 c-darkgray'>({tableData.length})</span> : null}
        </div>
        <div className='df fxdrr aic'>
          <span>Contract Year:</span>
          <div className='df aic jcc w60 h40 bgc-lightgray p10 bdrs-4 ml8'>
            {currentProductionYear ? <span>{currentProductionYear}</span> : <div className='loader h20 w20' />}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {
        hasPermission ?
          <div>
            <ButtonRow />
            <WarningModal arrOfTicketWithInvalidDates={arrOfTicketWithInvalidDates} arrOfDupVals={arrOfDupVals} showWarningModal={showWarningModal} setShowWarningModal={setShowWarningModal} numberOfIssues={numberOfIssues} arrOfMissingRequiredVal={arrOfMissingRequiredVal} />
            <WrongFormatModal showWrongFormatModal={showWrongFormatModal} setShowWrongFormatModal={setShowWrongFormatModal} />
            <CheckGrowerModal clear={clear} tableData={tableData} toggleModal={toggleCheckGrowerModalVisibility} setTableData={setTableData} visibility={checkGrowerModalVisibility} setPageRefresh={setPageRefresh} allScaleTickets={allScaleTickets} ticketsLoaded={ticketsLoaded}/>
            <ScaleTicketsHeader />
            {isLoading ?
              <div className='h100% w100% df aic jcc mt40'>
                <div className='loader h80 w80'/>
              </div> :
              tableData ? <div className="scale_ticket_sec">
                <Table data={tableData} structure={structure} />
              </div>
                : <div className="scale_ticket_before_sec"><Table statusComponentHeight='h400' structure={initialStructure} statusComponent={<TableStatus status={'EMPTY'} prompt={prompt} />} /></div>
            }
          </div> :
          <>
            <AccessDeniedView />
          </>
      }
    </>
  )
}

export default DocUploader
